import React from "react";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../redux";
import EngagedUpgradeModal from "./EngagedUpgradeModal";

const STORAGE_KEY = "engagedUpgradeShown";
const PAGE_THRESHOLD = 50;

/**
 * Singleton watcher mounted at app shell. While the user is trialing, it
 * tracks `user.credits` changes (which `JobWSUpdater` refreshes via
 * `fetchUser` after every Succeeded/Failed/Canceled/OutOfCredits event).
 * When cumulative pages used >= 50 and we have not yet shown the modal
 * for this user, open it once and stamp localStorage.
 */
const EngagedUpgradeWatcher: React.FC = () => {
  const subscription = useSelector(
    (state: ReduxState) => state.Subscription.subscription
  );
  const user = useSelector((state: ReduxState) => state.User.user);

  const [show, setShow] = React.useState(false);
  const [pagesUsed, setPagesUsed] = React.useState(0);
  const prevCreditsRef = React.useRef<number | null>(null);

  const isTrial = subscription?.status === "trialing";
  const trialNumPages = subscription?.docuclipperPlan?.trialNumPages ?? null;
  const credits = user?.credits ?? null;

  React.useEffect(() => {
    if (!isTrial || trialNumPages == null || credits == null) {
      prevCreditsRef.current = credits;
      return;
    }
    const prev = prevCreditsRef.current;
    prevCreditsRef.current = credits;

    // Only fire on a credit DROP (job just consumed pages). On first read or
    // an unchanged value, just record and bail. This avoids firing on page
    // refresh when the threshold was already crossed.
    if (prev == null || credits >= prev) {
      return;
    }

    const used = Math.max(trialNumPages - credits, 0);
    if (used < PAGE_THRESHOLD) {
      return;
    }

    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setPagesUsed(used);
    setShow(true);
  }, [isTrial, trialNumPages, credits]);

  if (!show) return null;
  return (
    <EngagedUpgradeModal
      show={show}
      pagesUsed={pagesUsed}
      onClose={() => setShow(false)}
    />
  );
};

export default EngagedUpgradeWatcher;
